import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import PremiumFullScreenOverlay from "../overlay/PremiumFullScreenOverlay";
import PremiumLoadingCard from "./PremiumLoadingCard";

// #region SCOPED CSS
const Overlay = styled(PremiumFullScreenOverlay)`
    .fullscreen-loader { inset: 0; height: auto; box-sizing: border-box; padding: 16px;
        background: rgba(8,9,9,0.80); overflow-y: auto; overscroll-behavior: contain; }
    .premium-export-progress { width: 360px; max-width: 100%; min-width: 0; outline: none; }
`;
// #endregion

/** Presentation of existing per-order operations. No requests, business state or dismiss action. */
export default function PremiumExportProgress({ orderNumber }) {
    const panel = useRef(null), portalRoot = useRef(null);
    useEffect(() => {
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        // Make the existing page inert without unmounting it or changing any card state.
        const background = [...document.body.children].filter(element => element !== portalRoot.current && !element.contains(portalRoot.current) &&
            !["STYLE", "SCRIPT"].includes(element.tagName));
        const prior = background.map(element => [element, element.hasAttribute("inert")]);
        for (const [element] of prior) element.setAttribute("inert", "");
        document.body.style.overflow = "hidden";
        panel.current?.focus();
        const containFocus = event => { if (!panel.current?.contains(event.target)) panel.current?.focus(); };
        const keys = event => {
            if (event.key === "Escape" || event.key === "Tab") {
                event.preventDefault(); event.stopImmediatePropagation(); panel.current?.focus();
            }
        };
        document.addEventListener("focusin", containFocus);
        window.addEventListener("keydown", keys, true);
        return () => {
            document.removeEventListener("focusin", containFocus);
            window.removeEventListener("keydown", keys, true);
            for (const [element, wasInert] of prior) if (!wasInert) element.removeAttribute("inert");
            document.body.style.overflow = previousOverflow;
            if (previousFocus?.isConnected && previousFocus !== document.body) previousFocus.focus();
        };
    }, []);
    return createPortal(<div ref={portalRoot}>
        <Overlay show overlayZIndex={100000}>
            <div ref={panel} className="premium-export-progress" role="dialog" aria-modal="true" aria-busy="true"
                aria-label="Export in progress" tabIndex={-1}>
                <PremiumLoadingCard title="EXPORTING..." header={orderNumber ? `Order #${orderNumber}` : "Exporting order"}
                    footer="Please wait for the confirmed result." />
            </div>
        </Overlay>
    </div>, document.body);
}
